import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRescheduleProposal } from "@/hooks/useRescheduleMeeting";
import { Loader2, Calendar as CalendarIcon, Clock, Plus, X, Globe, AlertCircle, RefreshCw } from "lucide-react";
import { format, addMinutes, setHours, setMinutes, isBefore, startOfToday } from "date-fns";
import { fromZonedTime, formatInTimeZone } from "date-fns-tz";
import { toast } from "sonner";
import { TIMEZONE_OPTIONS, timezoneLabel } from "@/lib/timezones";

interface RescheduleMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: string;
  threadId: string;
  otherPersonName: string;
  currentScheduledTime?: string | null;
}

interface TimeSlot {
  id: string;
  date: Date;
  time: string;
}

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "60 min" },
];

const TIME_OPTIONS = Array.from({ length: 28 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8; // 8 AM to 9 PM
  const minute = (i % 2) * 30;
  const time24 = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  const date = setMinutes(setHours(new Date(), hour), minute);
  const time12 = format(date, "h:mm a");
  return { value: time24, label: time12 };
});


export function RescheduleMeetingDialog({
  open,
  onOpenChange,
  meetingId,
  threadId,
  otherPersonName,
  currentScheduledTime,
}: RescheduleMeetingDialogProps) {
  const [duration, setDuration] = useState("30");
  const [timezone, setTimezone] = useState("America/Jamaica");
  const [note, setNote] = useState("");
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const rescheduleProposal = useRescheduleProposal();

  const MIN_BUFFER_MINUTES = 5;

  const getSlotDateUTC = (): Date | null => {
    if (!selectedDate || !selectedTime) return null;
    
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const hour = String(hours).padStart(2, "0");
    const min = String(minutes).padStart(2, "0");
    
    const localDateTimeString = `${year}-${month}-${day}T${hour}:${min}:00`;
    const utcDate = fromZonedTime(localDateTimeString, timezone);
    
    return utcDate;
  };

  const getValidationError = (): string | null => {
    if (!selectedDate || !selectedTime) return null;
    
    const slotDateUTC = getSlotDateUTC();
    if (!slotDateUTC) return null;
    
    const now = new Date();
    const minValidTime = addMinutes(now, MIN_BUFFER_MINUTES);
    
    if (isBefore(slotDateUTC, now)) {
      return "Please choose a time in the future.";
    }
    
    if (isBefore(slotDateUTC, minValidTime)) {
      return "Please choose a time at least 5 minutes from now.";
    }
    
    const exists = slots.some(
      (s) => s.date.getTime() === slotDateUTC.getTime()
    );
    if (exists) {
      return "This time slot is already added.";
    }
    
    return null;
  };

  const validationError = getValidationError();
  const canAddSlot = selectedDate && selectedTime && !validationError && slots.length < 5;

  const handleAddSlot = () => {
    if (!canAddSlot) return;

    const slotDateUTC = getSlotDateUTC();
    if (!slotDateUTC) return;

    const slotId = crypto.randomUUID();

    setSlots([
      ...slots,
      {
        id: slotId,
        date: slotDateUTC,
        time: selectedTime,
      },
    ]);

    setSelectedDate(undefined);
    setSelectedTime("");
  };

  const handleRemoveSlot = (id: string) => {
    setSlots(slots.filter((s) => s.id !== id));
  };

  const handleSubmit = async () => {
    if (slots.length < 1) return;

    setSubmitError(null);

    const durationMinutes = parseInt(duration);
    const formattedSlots = slots.map((slot) => ({
      start_at: slot.date.toISOString(),
      end_at: addMinutes(slot.date, durationMinutes).toISOString(),
    }));

    const loadingToastId = toast.loading("Submitting reschedule request...");

    try {
      await rescheduleProposal.mutateAsync({
        meetingId,
        threadId,
        durationMinutes,
        timezone,
        note: note.trim() || undefined,
        slots: formattedSlots,
      });

      toast.dismiss(loadingToastId);
      toast.success("Reschedule proposal sent!");
      
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.dismiss(loadingToastId);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("[RescheduleMeetingDialog] Failed:", error);
      
      setSubmitError(errorMessage);
      toast.error(`Failed to propose reschedule: ${errorMessage}`);
    }
  };

  const resetForm = () => {
    setDuration("30");
    setTimezone("America/Jamaica");
    setNote("");
    setSlots([]);
    setSelectedDate(undefined);
    setSelectedTime("");
    setSubmitError(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Reschedule Meeting
          </DialogTitle>
          <DialogDescription>
            Propose new time slots to reschedule your meeting with {otherPersonName}. 
            {currentScheduledTime && (
              <span className="block mt-1 text-muted-foreground">
                Current time: {format(new Date(currentScheduledTime), "EEEE, MMMM d 'at' h:mm a")}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Duration */}
          <div className="space-y-2">
            <Label>Meeting Duration</Label>
            <RadioGroup
              value={duration}
              onValueChange={setDuration}
              className="flex flex-wrap gap-3"
            >
              {DURATION_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`resched-dur-${opt.value}`} />
                  <Label htmlFor={`resched-dur-${opt.value}`} className="font-normal cursor-pointer">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Globe className="h-4 w-4" />
              Timezone
            </Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {timezoneLabel(tz)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-3">
            <Label>Propose New Time Slots (2-5 recommended)</Label>
            
            <div className="flex flex-col gap-4">
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => isBefore(date, startOfToday())}
                  className="rounded-md border pointer-events-auto"
                />
              </div>
              
              <div className="space-y-3">
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px] z-50">
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleAddSlot}
                  disabled={!canAddSlot}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Time Slot
                </Button>
                
                {validationError && selectedDate && selectedTime && (
                  <p className="text-xs text-destructive text-center">
                    {validationError}
                  </p>
                )}
                
                {slots.length > 0 && (
                  <div className="text-xs text-muted-foreground text-center">
                    {slots.length}/5 slots added
                  </div>
                )}
              </div>
            </div>

            {/* Selected Slots */}
            {slots.length > 0 && (
              <div className="space-y-2 mt-4">
                <Label className="text-sm text-muted-foreground">Proposed New Times:</Label>
                <div className="flex flex-wrap gap-2">
                  {slots
                    .sort((a, b) => a.date.getTime() - b.date.getTime())
                    .map((slot) => (
                      <Badge
                        key={slot.id}
                        variant="secondary"
                        className="py-1.5 px-3 gap-2"
                      >
                        <Clock className="h-3 w-3" />
                        {format(slot.date, "EEE, MMM d 'at' h:mm a")}
                        <button
                          onClick={() => handleRemoveSlot(slot.id)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="reschedule-note">Reason for reschedule (Optional)</Label>
            <Textarea
              id="reschedule-note"
              placeholder="e.g., 'Something came up, can we try one of these times instead?'"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Error display */}
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {submitError}
              </AlertDescription>
            </Alert>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
            <p>
              <strong>Note:</strong> The original meeting will remain active until {otherPersonName} accepts 
              one of the new times. If they decline, the original meeting stays scheduled.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={rescheduleProposal.isPending || slots.length < 1}
          >
            {rescheduleProposal.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Propose Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
